/**
 * Registro dei salvataggi del dialogo "Dati e relazioni" (360).
 *
 * Nel dialogo NON esiste un salva per sezione: c'è un unico salva globale nel footer. Ogni
 * sotto-vista editabile (form inline, selezione CompanyYear, …) si limita a dichiarare alla shell
 * "ho modifiche non salvate" e "ecco come salvarle", tramite `useRegisterEditSave`. La shell mostra
 * il badge "non salvato" sulla card della sezione e, al Salva, esegue in sequenza tutti gli handle
 * sporchi prima di applicare le relazioni.
 *
 * Sta in un modulo a parte per non creare un ciclo di import fra `relations360Shared` (la shell) e
 * `CompanyData360EditView` (le sotto-viste).
 */

import React, { useContext, useEffect, useRef } from 'react';

/** Stato di salvataggio pubblicato da una sotto-vista verso la shell del dialogo. */
export interface EditSaveHandle {
	isDirty: boolean;
	// risolve con `false` se il salvataggio fallisce (validazione o errore server)
	save: () => Promise<any>;
}

export type RegisterEditSave = (
	id: string,
	sectionKey: string,
	handle: EditSaveHandle | null,
) => void;

export interface SubFormHandle {
	isDirty: boolean;
	// 'create' → azione primaria "Crea"; 'edit' → "Salva"
	kind: 'create' | 'edit';
	// salva SOLO l'entità di questa sotto-vista; risolve con `false` se la validazione fallisce
	submit: () => Promise<any>;
	// torna alla vista precedente scartando il form
	cancel: () => void;
}

export type RegisterSubForm = (id: string, handle: SubFormHandle | null) => void;

const RegistryContext = React.createContext<RegisterEditSave | null>(null);
const SectionKeyContext = React.createContext<string>('');
const SubFormContext = React.createContext<RegisterSubForm | null>(null);

/** Wrapper con cui la shell monta una sotto-vista, associandola alla sua sezione. */
export const EditSaveScope: React.FC<{
	sectionKey: string;
	register: RegisterEditSave;
	children?: React.ReactNode;
}> = ({ sectionKey, register, children }) => (
	<RegistryContext.Provider value={register}>
		<SectionKeyContext.Provider value={sectionKey}>
			{children}
		</SectionKeyContext.Provider>
	</RegistryContext.Provider>
);

/** Provider unico attorno al corpo del dialogo: raccoglie le sotto-viste a singola entità. */
export const SubFormScope: React.FC<{
	register: RegisterSubForm;
	children?: React.ReactNode;
}> = ({ register, children }) => (
	<SubFormContext.Provider value={register}>{children}</SubFormContext.Provider>
);

let handleSeq = 0;

/**
 * Dichiara alla shell del dialogo 360 lo stato di salvataggio di questa sotto-vista.
 * Fuori dal dialogo (nessun provider) è un no-op, così i componenti restano riutilizzabili.
 */
export const useRegisterEditSave = (handle: EditSaveHandle): void => {
	const register = useContext(RegistryContext);
	const sectionKey = useContext(SectionKeyContext);
	const idRef = useRef<string>('');
	if (!idRef.current) {
		idRef.current = 'edit-save-' + ++handleSeq;
	}

	// ri-registro ad ogni render: l'handle cattura `submit`/`isDirty` sempre aggiornati
	useEffect(() => {
		if (register) {
			register(idRef.current, sectionKey, handle);
		}
	});

	useEffect(
		() => () => {
			if (register) {
				register(idRef.current, sectionKey, null);
			}
		},
		[register, sectionKey],
	);
};

/**
 * Dichiara alla shell che questa sotto-vista sta manipolando una singola entità, così il footer
 * del dialogo sostituisce il Salva globale con le sole azioni di questo form.
 * Passare `null` quando la sotto-vista non è attiva (l'hook va chiamato comunque, ad ogni render).
 */
export const useRegisterSubForm = (handle: SubFormHandle | null): void => {
	const register = useContext(SubFormContext);
	const idRef = useRef<string>('');
	if (!idRef.current) {
		idRef.current = 'sub-form-' + ++handleSeq;
	}

	// ri-registro ad ogni render: l'handle cattura `submit`/`isDirty` sempre aggiornati
	useEffect(() => {
		if (register) {
			register(idRef.current, handle);
		}
	});

	useEffect(
		() => () => {
			if (register) {
				register(idRef.current, null);
			}
		},
		[register],
	);
};
